/* eslint-disable @typescript-eslint/no-explicit-any */
// Base64 bez btoa (żeby uniknąć deprecacji)
function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined' && (Buffer as any).from) {
    return Buffer.from(bytes).toString('base64');
  }
  const enc =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let out = '',
    i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      enc[(n >>> 18) & 63] +
      enc[(n >>> 12) & 63] +
      enc[(n >>> 6) & 63] +
      enc[n & 63];
  }
  if (i === bytes.length - 1) {
    const n = bytes[i] << 16;
    out += enc[(n >>> 18) & 63] + enc[(n >>> 12) & 63] + '==';
  } else if (i === bytes.length - 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out +=
      enc[(n >>> 18) & 63] + enc[(n >>> 12) & 63] + enc[(n >>> 6) & 63] + '=';
  }
  return out;
}

//  DeviceSignatureService — uzyskanie "key" z urządzenia (FFA1/FFA2)
//  Algorytm: md5(pliku) -> weź środkowe 16 znaków -> zapisz ASCII do FFA2 -> odczytaj bytes -> Base64 = key
export class ECGDeviceSignatureService {
  static readonly SVC = '0000ffa1-0000-1000-8000-00805f9b34fb';
  static readonly CH = '0000ffa2-0000-1000-8000-00805f9b34fb';

  private device?: BluetoothDevice;
  private server?: BluetoothRemoteGATTServer;
  private char?: BluetoothRemoteGATTCharacteristic;
  static instance: ECGDeviceSignatureService;

  // wywołaj po udanym connect()
  async bind(server: BluetoothRemoteGATTServer) {
    this.server = server;
    this.device = server.device;
    // czyść cache po disconnect
    this.device.addEventListener('gattserverdisconnected', () => {
      this.char = undefined;
      this.server = undefined;
    });
    await this.refreshCharacteristic();
  }

  public static getInstance() {
    if (!ECGDeviceSignatureService.instance) {
      ECGDeviceSignatureService.instance = new ECGDeviceSignatureService();
    }

    return ECGDeviceSignatureService.instance;
  }

  // zadba o reconnect + pobranie characteristic
  private async refreshCharacteristic() {
    // reconnect jeśli trzeba
    if (!this.server || !this.server.connected) {
      if (!this.device) {
        throw new Error('No BLE device bound');
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.server = await this.device.gatt!.connect();
    }
    // pobierz charakterystykę od nowa
    const svc = await this.server.getPrimaryService(
      ECGDeviceSignatureService.SVC as any,
    );
    this.char = await svc.getCharacteristic(
      ECGDeviceSignatureService.CH as any,
    );
  }

  // helper: upewnij się, że mamy ważną charakterystykę
  private async ensureChar() {
    if (!this.char) {
      await this.refreshCharacteristic();
      return;
    }
    // szybki test: dostęp do properties zwykle nie rzuca, ale startNotifications zweryfikuje realnie
  }

  async getKeyFromMid16(
    mid16: string,
    opts?: { uppercase?: boolean; timeoutMs?: number },
  ): Promise<string> {
    const { uppercase = true, timeoutMs = 7000 } = opts || {};
    if (!/^[0-9a-fA-F]{16}$/.test(mid16)) {
      throw new Error('mid16 must be 16 hex chars');
    }

    await this.ensureChar();

    const payload = uppercase ? mid16.toUpperCase() : mid16.toLowerCase();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ch = this.char!;

    // Funkcja, która zrobi startNotifications + write + czeka na odpowiedź
    const once = async (): Promise<Uint8Array> => {
      await ch.startNotifications();
      const resp = new Promise<Uint8Array>((resolve, reject) => {
        const to = setTimeout(() => {
          ch.removeEventListener('characteristicvaluechanged', onChange as any);
          reject(new Error('FFA2 timeout'));
        }, timeoutMs);
        const onChange = (ev: any) => {
          clearTimeout(to);
          const dv: DataView = ev.target.value;
          const out = new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength);
          ch.removeEventListener('characteristicvaluechanged', onChange as any);
          resolve(out);
        };
        ch.addEventListener('characteristicvaluechanged', onChange as any);
      });
      await ch.writeValue(new TextEncoder().encode(payload));
      return await resp;
    };

    // Pierwsza próba
    try {
      const bytes = await once();
      return bytesToBase64(bytes);
    } catch (e: any) {
      // Jeśli „no longer valid” – odśwież charakterystykę i spróbuj raz jeszcze
      const msg = String(e?.message || e);
      if (
        msg.includes('no longer valid') ||
        msg.includes('InvalidStateError')
      ) {
        await this.refreshCharacteristic();
        const bytes = await once();
        return bytesToBase64(bytes);
      }
      throw e;
    }
  }
}
